import React, { useEffect, useState } from 'react';
import { Button, Flex, FlexItem, Label, Spinner, Tooltip } from '@patternfly/react-core';
import { MagicIcon, OutlinedQuestionCircleIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { rrsetErrorMessage } from '../Domains/zoneRecords';
import type { DkimKeyDetail } from '../../../types/models/DkimKey';
import type { ApiResponseDkimKeyGet } from '../../../types/api/ApiResponseDkimKey';
import type { ApiResponseDnsRrsetWrite } from '../../../types/api/ApiResponseDomainName';

interface DkimDnsStatusProps {
  mailDomainId: number;
  selector: string;
  /**
   * The managed DNS zone's `domain_name_id`, or `null` when the domain's zone isn't managed here. The
   * one-click "Fix" (publish the DKIM TXT record) is only offered when this is non-null.
   */
  domainNameId: number | null;
  /** Bump to force a re-check (wired to the tab's Refresh action). */
  refreshToken: number;
}

interface DnsState {
  loading: boolean;
  key: DkimKeyDetail | null;
  error: string | null;
}

/**
 * Per-selector DNS publication status, shown next to the health-check columns. The Get DKIM read is an
 * SSH + DNS lookup, so it runs lazily in the background once this cell mounts — i.e. after the selectors
 * table has loaded and rendered — surfacing a small spinner meanwhile. The published/match flags are a
 * best-effort signal from the backend (`dns_checked` false ⇒ couldn't determine; see `dns_error`).
 *
 * When the domain's DNS zone is managed here (`domainNameId` non-null) and the record is missing
 * ("Not published") or wrong ("Key mismatch"), a one-click **Fix** publishes the key's TXT record to the
 * managed zone via the generic rrset endpoint — the same write the SPF/DMARC wizards use. The backend's
 * RecordContentValidator chunks the long DKIM value into ≤255-octet TXT strings, so the raw
 * `dns_record_value` is sent as-is.
 */
function DkimDnsStatus({ mailDomainId, selector, domainNameId, refreshToken }: DkimDnsStatusProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [state, setState] = useState<DnsState>({ loading: true, key: null, error: null });
  const [publishing, setPublishing] = useState(false);
  // Optimistic flag after a successful publish. The host-side check resolves via a (possibly caching)
  // resolver, so a negative cache could briefly report "Not published" right after we write the record —
  // which we just published with the exact value, so dns_published/dns_match are guaranteed true. Render
  // "Published" immediately; a manual Refresh (refreshToken bump) clears this and re-checks truthfully.
  const [justPublished, setJustPublished] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, key: null, error: null });
    setJustPublished(false);

    (async () => {
      try {
        const response = await apiFetch(`/emails/domains/${mailDomainId}/dkim?selector=${encodeURIComponent(selector)}`);
        const body = (await response.json().catch(() => null)) as ApiResponseDkimKeyGet | null;
        if (cancelled) {
          return;
        }
        if (body?.status === 'success') {
          setState({ loading: false, key: body.data?.dkim_key ?? null, error: null });
        } else {
          setState({ loading: false, key: null, error: body?.message ?? 'The DNS check could not run.' });
        }
      } catch (err) {
        if (cancelled) {
          return;
        }
        setState({ loading: false, key: null, error: err instanceof Error ? err.message : 'The DNS check could not run.' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiFetch, mailDomainId, selector, refreshToken]);

  // Publish (or replace, for a mismatch) the selector's `_domainkey` TXT record into the managed zone.
  // The set holds exactly this one DKIM record, so a create is the normal path; if a stale/mismatched
  // set already exists, the POST collides and we replace it in place via PATCH.
  const handleFix = async () => {
    const key = state.key;
    if (domainNameId === null || key === null) {
      return;
    }
    setPublishing(true);

    const payload = {
      name: key.dns_record_name,
      type: 'TXT',
      ttl: 3600,
      content: [key.dns_record_value],
    };
    const send = (method: 'POST' | 'PATCH') =>
      apiFetch(`/datacenters/domain-names/${domainNameId}/dns/rrset`, {
        method,
        headers: { Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
    const notifyFailure = (reason: string) => {
      addNotification({
        title: 'Could not publish DKIM record',
        srTitle: 'Error alert',
        description: reason,
        variant: 'danger',
      });
      setDrawerOpen(true);
    };

    try {
      let response = await send('POST');
      let body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;

      const collided =
        body?.status !== 'success' &&
        typeof body?.data?.name === 'string' &&
        body.data.name.toLowerCase().includes('already exists');
      if (collided) {
        response = await send('PATCH');
        body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetWrite | null;
      }

      if (body?.status === 'success' && body.data?.record_set) {
        setJustPublished(true);
        addNotification({
          title: 'DKIM record published',
          srTitle: 'Success alert',
          description: `The DKIM record for selector “${selector}” was published to DNS.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        return;
      }
      notifyFailure(rrsetErrorMessage(body) ?? `Request failed with status ${response.status}`);
    } catch (err) {
      notifyFailure(err instanceof Error ? err.message : 'An unexpected error occurred while publishing.');
    } finally {
      setPublishing(false);
    }
  };

  // The "Fix" affordance — only when the zone is managed here. Pairs with the warning label so the user
  // can publish without leaving the tab.
  const fixButton =
    domainNameId !== null ? (
      <Button
        variant="link"
        isInline
        icon={<MagicIcon />}
        onClick={() => void handleFix()}
        isLoading={publishing}
        isDisabled={publishing}
        spinnerAriaLabel={`Publishing the DKIM record for ${selector}`}
        aria-label={`Publish the DKIM record for ${selector} to DNS`}
      >
        Fix
      </Button>
    ) : null;

  const withFix = (label: React.ReactNode) =>
    fixButton === null ? (
      label
    ) : (
      <Flex gap={{ default: 'gapSm' }} alignItems={{ default: 'alignItemsCenter' }}>
        <FlexItem>{label}</FlexItem>
        <FlexItem>{fixButton}</FlexItem>
      </Flex>
    );

  if (state.loading) {
    return <Spinner size="sm" aria-label={`Checking DNS publication for ${selector}`} />;
  }

  // Just published — render the live state optimistically as published (see justPublished above).
  if (justPublished) {
    return (
      <Label status="success" isCompact>
        Published
      </Label>
    );
  }

  const key = state.key;

  // Couldn't determine — fetch error, no key returned, or the host couldn't run the check.
  // The reason is mirrored in screen-reader-only text so it isn't lost to AT (a tooltip on a static
  // Label is not reliably announced); sighted users still get it on hover. No Fix is offered: we don't
  // know the record is actually wrong, so publishing blindly would be presumptuous.
  if (state.error || !key || !key.dns_checked) {
    const reason = state.error ?? key?.dns_error ?? 'The DNS publication check could not run.';
    return (
      <Tooltip content={reason}>
        <Label color="grey" isCompact icon={<OutlinedQuestionCircleIcon />}>
          Unchecked
          <span className="pf-v6-u-screen-reader">: {reason}</span>
        </Label>
      </Tooltip>
    );
  }

  if (!key.dns_published) {
    const reason = `No DKIM record is published at ${key.dns_lookup_name}.`;
    return withFix(
      <Tooltip content={reason}>
        <Label status="warning" isCompact>
          Not published
          <span className="pf-v6-u-screen-reader">: {reason}</span>
        </Label>
      </Tooltip>,
    );
  }

  if (!key.dns_match) {
    const reason = `A different DKIM key is published at ${key.dns_lookup_name} than the one on the mail server.`;
    return withFix(
      <Tooltip content={reason}>
        <Label status="warning" isCompact>
          Key mismatch
          <span className="pf-v6-u-screen-reader">: {reason}</span>
        </Label>
      </Tooltip>,
    );
  }

  return (
    <Label status="success" isCompact>
      Published
    </Label>
  );
}

export default DkimDnsStatus;
